import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Typography, Space, Select, Tabs, DatePicker } from 'antd';
import moment from 'moment';
const { RangePicker } = DatePicker;
import { StyledHorizontalTabs } from '../../Style/style';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getJobtypeData, getFinancialYearData } from '../../../actions';
import { useSelector, useDispatch } from 'react-redux';
import Button from 'components/Button'

const { Text } = Typography;

const DashboardFilters = ({ selectedFilter, setSelectedFilter, onFinancialYearChange, initialFinancialYear }) => {
  const dispatch = useDispatch();
  const jobtypeData = useSelector(state => state?.corporateDashboardPage2?.jobtypeData);
  const financialYearData = useSelector(state => state?.corporateDashboardPage2?.financialYearData);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);
  const [appliedFinancialYear, setAppliedFinancialYear] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDateSelecting, setIsDateSelecting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const rangePickerRef = useRef(null);

  // Convert jobtype to tab configuration
  const convertJobtypeToTab = (jobtype) => {
    switch (jobtype) {
      case "PLACEMENT":
        return { key: "JOBS", label: "Roles" };
      case "INTERNSHIP":
        return { key: "INTERNSHIP", label: "Internships" };
      case "APPRENTICESHIP":
        return { key: "APPRENTICESHIP", label: "Apprenticeship" };
      case "INTERNSHIP_PPO":
        return { key: "INTERNSHIP_PPO", label: "Internship + PPO" };
      case "GIGS":
        return { key: "PROJECTS", label: "Projects" };
      default:
        return null;
    }
  };

  // Function to get financial year based on date
  // Financial year: April 1 to March 31
  const getFinancialYear = (date) => {
    const currentDate = new Date(date);
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // Months are 0-indexed
    
    // If date is before April 1, it belongs to previous financial year
    if (currentMonth < 4) {
      return `${currentYear - 1} - ${currentYear}`;
    } else {
      return `${currentYear} - ${currentYear + 1}`;
    }
  };

  // Function to convert year range to date range for RangePicker
  const getFinancialYearWithMonths = (year) => {
    // Convert year (e.g., 2026) to financial year date range
    return {
      start: moment(`${year}-04-01`), // April 1 of the year
      end: moment(`${year + 1}-03-31`) // March 31 of next year
    };
  };

  // Function to generate financial year options based on API data
  const getFinancialYearOptions = () => {
    if (!financialYearData?.years || financialYearData.years.length === 0) {
      // Fallback to static options if API data is not available
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      
      let baseYear = currentYear;
      if (currentMonth < 4) {
        baseYear = currentYear - 1;
      }
      
      const options = [];
      for (let i = 0; i < 4; i++) {
        const year = baseYear - i;
        const dateRange = getFinancialYearWithMonths(year);
        options.push({
          value: `${year} - ${year + 1}`,
          label: `April ${year} - March ${year + 1}`,
          dateRange: [dateRange.start, dateRange.end]
        });
      }
      
      return options;
    }
    
    // Generate options based on API response years
    return financialYearData.years.map(year => {
      const dateRange = getFinancialYearWithMonths(year);
      return {
        value: `${year} - ${year + 1}`,
        label: `April ${year} - March ${year + 1}`,
        dateRange: [dateRange.start, dateRange.end]
      };
    });
  };

  // Initialize selected financial year on component mount
  useEffect(() => {
    if (initialFinancialYear) {
      setSelectedFinancialYear(initialFinancialYear);
      setAppliedFinancialYear(initialFinancialYear);
    } else {
      // Fallback to current financial year if no initial value provided
      const currentYear = moment().year();
      const currentMonth = moment().month() + 1; // Months are 0-indexed
      
      let financialYear;
      if (currentMonth < 4) {
        financialYear = currentYear - 1;
      } else {
        financialYear = currentYear;
      }
      
      const dateRange = getFinancialYearWithMonths(financialYear);
      setSelectedFinancialYear([dateRange.start, dateRange.end]);
      setAppliedFinancialYear([dateRange.start, dateRange.end]);
    }
  }, [initialFinancialYear]);

  const handleFinancialYearChange = (value) => {
    setIsDateSelecting(true);
    setIsDropdownOpen(true);
    
    if (value && Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
      // Set end date to last day of selected month
      const endDate = moment(value[1]).endOf('month');
      const correctedValue = [value[0], endDate];
      setSelectedFinancialYear(correctedValue);
      
      // Check if selection differs from applied selection
      if (!appliedFinancialYear || 
          !moment(appliedFinancialYear[0]).isSame(correctedValue[0]) || 
          !moment(appliedFinancialYear[1]).isSame(correctedValue[1])) {
        setHasChanges(true);
      } else {
        setHasChanges(false);
      }
    } else {
      setSelectedFinancialYear(value);
      setHasChanges(false);
    }
    setTimeout(() => setIsDateSelecting(false), 1000);
  };

  const handleApplyDateRange = () => {
    setIsDateSelecting(false);
    setIsDropdownOpen(false);
    if (selectedFinancialYear && Array.isArray(selectedFinancialYear) && selectedFinancialYear.length === 2) {
      setAppliedFinancialYear(selectedFinancialYear);
      setHasChanges(false);
      onFinancialYearChange(selectedFinancialYear);
    }
  };

  // Fetch jobtype and financial year data on component mount
  useEffect(() => {
    dispatch(getJobtypeData());
    dispatch(getFinancialYearData());
  }, [dispatch]);

  // Define the desired order for tabs
  const tabOrder = ["JOBS", "INTERNSHIP", "APPRENTICESHIP", "PROJECTS", "INTERNSHIP_PPO"];

  // Update available tabs when jobtype data changes
  useEffect(() => {
    if (jobtypeData?.jobtypes) {
      const tabs = jobtypeData.jobtypes
        .map(convertJobtypeToTab)
        .filter(tab => tab !== null);
      // Sort tabs according to the defined order
      const sortedTabs = tabs.sort((a, b) => {
        return tabOrder.indexOf(a.key) - tabOrder.indexOf(b.key);
      });
      setAvailableTabs(sortedTabs);
    }
  }, [jobtypeData]);

  const handleTabChange = (activeKey) => {
    setSelectedFilter(activeKey);
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginTop: "5px" }}>
        <Col>
          <StyledHorizontalTabs 
            defaultActiveKey={selectedFilter} 
            onChange={handleTabChange}
          >
            {availableTabs.map(tab => (
              <Tabs.TabPane tab={tab.label} key={tab.key} />
            ))}
          </StyledHorizontalTabs>
        </Col>
        <Col>
          <Space align="center">
            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#000000ff' }}>Select Financial Year</Text>
            <RangePicker
              ref={rangePickerRef}
              picker="month"
              value={selectedFinancialYear}
              onChange={handleFinancialYearChange}
              open={isDropdownOpen}
              onOpenChange={(open) => {
                console.log('onOpenChange called with:', open, 'isDateSelecting:', isDateSelecting);
                // Only allow closing if not in the middle of date selection
                if (!open && isDateSelecting) {
                  // Prevent closing during date selection
                  setTimeout(() => setIsDropdownOpen(true), 0);
                  return;
                }
                setIsDropdownOpen(open);
              }}
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              allowClear={false}
              placement="bottomLeft"
              renderExtraFooter={() => (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0' }}>
                  <Button.Primary
                    text="Apply"
                    onClick={handleApplyDateRange}
                    disabled={!hasChanges || !selectedFinancialYear || !Array.isArray(selectedFinancialYear) || selectedFinancialYear.length !== 2}
                  />
                </div>
              )}
              style={{ 
                width: 240,
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 600,
                fontStyle: 'Bold',
                fontSize: '12px',
                lineHeight: '18.51px',
                letterSpacing: '0px',
              }}
              placeholder={['Start month', 'End month']}
              format="MMM YYYY"
            />
          </Space>
        </Col>
      </Row>
      <Space align="start" style={{ marginBottom: 0, marginTop: '10px' }}> {/* Added margin */}
        <InfoCircleOutlined style={{ color: "#1852d0" }} />
        <Text style={{ fontSize: 14, color: '#262626' }}>
          Click on the <strong>legends</strong> below to get a detailed view.
        </Text>
      </Space>
    </>
  );
};

export default DashboardFilters;